import { forwardRef } from "react";
import type { FunctionComponent, Ref, SVGProps } from "react";
import ReactSelect, { mergeStyles, components as ReactSelectComponents } from "react-select";
import type {
  Props as ReactSelectProps,
  StylesConfig,
  ClassNamesConfig,
  GroupBase,
  OnChangeValue,
  ActionMeta,
  ClearIndicatorProps,
  SelectInstance,
} from "react-select";

import SelectorIcon from "@assets/icons/selector.svg";
import CloseRoundedIcon from "@assets/icons/close-icon-rounded.svg";

import { FieldErrorMessage } from "@ui/FormElements/FieldErrorMessage";

import { APP_COLORS } from "@styles/colors";
import useSystemStore from "@store/useSystemStore";

export function getCustomSelectStyles<OptionType>(isInvalid = false) {
  const { isDarkMode } = useSystemStore();

  return {
    control: (provided, { menuIsOpen, isFocused }) => {
      const isActive = menuIsOpen || isFocused;
      // Hover and focus are overridden too, so an invalid control can't lose its red on interaction.
      // action.danger is red600 — the same red as `border-red-600` on Input.
      const invalidBorder = { borderColor: APP_COLORS.action.danger };
      return {
        ...provided,
        borderRadius: 8,
        borderWidth: 1,
        fontSize: 14,
        borderColor: isActive
          ? isDarkMode
            ? APP_COLORS.stroke.actionPrimaryDark
            : APP_COLORS.stroke.actionPrimary
          : isDarkMode
            ? APP_COLORS.stroke.primaryDark
            : APP_COLORS.stroke.primary,
        backgroundColor: isDarkMode
          ? APP_COLORS.background.primaryDark
          : APP_COLORS.background.primary,
        color: isDarkMode ? APP_COLORS.text.primaryDark : APP_COLORS.text.primary,
        boxShadow: isActive
          ? "0px 0px 0px 3px #B9D6D1" // theme.boxShadow.mainButtonFocus
          : "none",
        ":hover": {
          borderColor: isDarkMode ? APP_COLORS.stroke.secondaryDark : APP_COLORS.stroke.secondary,
        },
        ":focus": {
          borderColor: isDarkMode
            ? APP_COLORS.stroke.actionPrimaryDark
            : APP_COLORS.stroke.actionPrimary,
        },
        ...(isInvalid && {
          ...invalidBorder,
          boxShadow: "none",
          ":hover": invalidBorder,
          ":focus": invalidBorder,
        }),
      };
    },
    singleValue: (provided) => ({
      ...provided,
      color: isDarkMode ? APP_COLORS.text.primaryDark : APP_COLORS.text.primary,
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: isDarkMode ? APP_COLORS.gray["750"] : APP_COLORS.background.tag,
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: isDarkMode ? APP_COLORS.text.primaryDark : APP_COLORS.text.primary,
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      backgroundColor: "transparent",
      color: APP_COLORS.gray["450"],
      ":hover": {
        backgroundColor: isDarkMode ? APP_COLORS.gray["700"] : APP_COLORS.gray["200"],
        color: isDarkMode ? APP_COLORS.text.primaryDark : APP_COLORS.text.primary,
      },
    }),
    option: (provided, { isSelected }) => ({
      ...provided,
      fontSize: 14,
      borderRadius: 8,
      backgroundColor: isSelected
        ? isDarkMode
          ? APP_COLORS.background.primaryDark
          : APP_COLORS.background.primary
        : "transparent",
      color: isDarkMode ? APP_COLORS.text.primaryDark : APP_COLORS.text.primary,
      ":hover": {
        backgroundColor: isDarkMode
          ? APP_COLORS.background.secondaryDark
          : APP_COLORS.background.secondary,
      },
    }),
    menu: (provided) => ({
      ...provided,
      borderWidth: 1,
      borderRadius: 8,
      padding: "0 4px",
      margin: "4px 0",
      borderColor: isDarkMode ? APP_COLORS.stroke.primaryDark : APP_COLORS.stroke.primary,
      backgroundColor: isDarkMode
        ? APP_COLORS.background.primaryDark
        : APP_COLORS.background.primary,
    }),
    placeholder: (provided) => ({
      ...provided,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      color: APP_COLORS.gray["400"],
      fontSize: 14,
    }),
    indicatorSeparator: () => ({ display: "none" }),
    clearIndicator: () => ({ padding: 0 }),
    input: (provided) => ({
      ...provided,
      "input:focus": {
        boxShadow: "none",
        border: "0",
      },
    }),
  } as StylesConfig<OptionType>;
}

export function getSelectCommonClasses<OptionType>(
  isMulti: ReactSelectProps["isMulti"]
): ClassNamesConfig<OptionType> {
  return {
    control: () => "!rounded-lg",
    valueContainer: () => "h-9 !py-0 !flex !flex-nowrap",
    input: () => (isMulti ? "" : "absolute"),
    menuPortal: () => "!z-[21]",
  };
}

export interface SelectProps<OptionType, IsMulti extends boolean = false> extends ReactSelectProps<
  OptionType,
  IsMulti
> {
  TriggerIcon?: FunctionComponent<SVGProps<SVGSVGElement>>;
  /** Paints the control red; pair with `errorMessage` so the border and the text agree. */
  isInvalid?: boolean;
  errorMessage?: string;
}

export type DefaultSelectOption = {
  value: string;
  label: string;
};

export const DropdownIndicator = () => (
  <SelectorIcon className="mr-1 text-gray-400" width={20} height={20} />
);

export const ClearIndicator = <Option, IsMulti extends boolean>(
  props: ClearIndicatorProps<Option, IsMulti, GroupBase<Option>>
) => {
  const { selectProps } = props;
  let hasValue: boolean;

  if (Array.isArray(selectProps.value)) {
    hasValue = selectProps.value.length > 0;
  } else {
    const singleValue = selectProps.value as { value?: string };
    hasValue = !!singleValue.value;
  }

  if (!hasValue) {
    return null;
  }

  return (
    <ReactSelectComponents.ClearIndicator {...props}>
      <div className="mr-1 cursor-pointer opacity-70 duration-200 hover:opacity-100">
        <CloseRoundedIcon width={16} height={16} color="#6B7080" />
      </div>
    </ReactSelectComponents.ClearIndicator>
  );
};

export const EmptyControl = ({ children, ...props }: any) => {
  return (
    <ReactSelectComponents.Control
      {...props}
      className="!max-h-0 !min-h-0 !border-0 !bg-transparent !shadow-none"
    >
      {children}
    </ReactSelectComponents.Control>
  );
};

function SelectComponent<OptionType = DefaultSelectOption, IsMulti extends boolean = false>(
  {
    isClearable = false,
    menuPlacement = "auto",
    menuPosition = "fixed",
    isMulti,
    styles,
    classNames,
    components,
    onChange,
    options,
    value,
    menuPortalTarget,
    isInvalid,
    errorMessage,
    ...rest
  }: SelectProps<OptionType, IsMulti>,
  ref: Ref<SelectInstance<OptionType, IsMulti>>
) {
  const customSelectStyles = getCustomSelectStyles<OptionType>(isInvalid || !!errorMessage);
  const customClasses = getSelectCommonClasses<OptionType>(isMulti);

  const handleChange = (
    newValue: OnChangeValue<OptionType, IsMulti>,
    actionMeta: ActionMeta<OptionType>
  ) => {
    if (ref && typeof ref !== "function" && ref?.current) {
      ref.current.blur();
    }

    if (onChange) {
      onChange(newValue, actionMeta);
    }
  };

  return (
    <>
      <ReactSelect
        ref={ref}
        isClearable={isClearable}
        isMulti={isMulti}
        aria-invalid={isInvalid || !!errorMessage || undefined}
        styles={mergeStyles(customSelectStyles, styles)}
        classNames={{ ...customClasses, ...classNames }}
        components={{
          ...components,
          DropdownIndicator,
          ClearIndicator: isClearable && value ? ClearIndicator : undefined,
        }}
        menuPlacement={menuPlacement}
        menuPosition={menuPosition}
        onChange={handleChange}
        options={options}
        value={value}
        menuPortalTarget={menuPortalTarget}
        {...rest}
      />
      <FieldErrorMessage message={errorMessage} />
    </>
  );
}

export const Select = forwardRef(SelectComponent) as <
  OptionType = DefaultSelectOption,
  IsMulti extends boolean = false,
>(
  props: SelectProps<OptionType, IsMulti> & { ref?: Ref<SelectInstance<OptionType, IsMulti>> }
) => JSX.Element;
